import { capitalizeEachWord, capitalize } from 'lib/utils/common/commonUtils'
import { hasLocizeTranslation } from 'lib/utils/locize'
import {
  BASE_CELL_WIDTH,
  BODY_PART_MEASUREMENT,
  CONTENT_ROW_PADDING,
  MEASUREMENT_TITLE_LOCIZE,
  MEASUREMENT_TYPE,
} from './const'
import { SIZE_GUIDES_TABLE } from './sizes-guide/const'

const translateMeasurementType = (measurementType, t) => {
  const defaultText = `${capitalize(measurementType)} Measurements`
  const translatedText = hasLocizeTranslation(
    t,
    MEASUREMENT_TITLE_LOCIZE[measurementType.toLowerCase()],
    defaultText,
  )

  return translatedText
}

const translateBodyPart = (bodyPartName, t) => {
  const defaultWord = capitalizeEachWord(bodyPartName)
  const keyWord = bodyPartName.split(' ').join('_')
  const translatedWord = hasLocizeTranslation(
    t,
    BODY_PART_MEASUREMENT[keyWord],
    defaultWord,
  )

  return translatedWord
}

const setupFieldName = (acc, i, name, t, type) => {
  if (!acc[name]) {
    const translatedBodyPart = translateBodyPart(name, t)

    if ([MEASUREMENT_TYPE.body, MEASUREMENT_TYPE.shoe].includes(type)) {
      acc[name] = { size: translatedBodyPart }
    } else {
      acc[name] = { size: `${i + 1}. ${translatedBodyPart}` }
    }
  }
}

const rowDataIteration = (
  dataList,
  { acc, iterateOnce, size, t, type, unit },
) => {
  // eslint-disable-next-line array-callback-return
  dataList.map(({ name, value, value_in }, i) => {
    setupFieldName(acc, i, name, t, type)

    // shoe products will have one product measurement value across all size
    // hence the field name will be empty string for invisible column header
    const sizeField = iterateOnce ? ' ' : size
    acc[name][sizeField] = (unit === 'in' ? value_in : value) || '-' // if it empty string make it hyphen
  })
}

const operationSelector = (variableProps, requiredProp) => {
  const { measurement_type_body, measurements, specList } = variableProps
  switch (requiredProp.type) {
    // support new second table policy
    case MEASUREMENT_TYPE.new_body:
      rowDataIteration(measurement_type_body, requiredProp)
      break
    case MEASUREMENT_TYPE.shoe: {
      const fixedUnitList = measurements.filter((item) =>
        specList.includes(item.name),
      )
      rowDataIteration(fixedUnitList, requiredProp)
      break
    }
    // support single table and product measurement table for the dual table policy
    case MEASUREMENT_TYPE.body:
    case MEASUREMENT_TYPE.product:
    default: {
      // if specList present means that we have to separate the fixed unit date from the convertible data
      const dataList = specList
        ? measurements.filter((item) => !specList.includes(item.name))
        : measurements
      rowDataIteration(dataList, requiredProp)
      break
    }
  }
}

const getTableRows = (
  attributes,
  t,
  type,
  unit,
  specList = null,
  iterateOnce = false,
) => {
  const measurementSerialize = attributes.reduce(
    (acc, { measurements, measurement_type_body, size }) => {
      const variableProps = {
        measurement_type_body: Array.isArray(measurement_type_body)
          ? measurement_type_body
          : [],
        measurements: Array.isArray(measurements) ? measurements : [],
        specList,
      }
      const requiredProp = { acc, iterateOnce, size, t, type, unit }
      operationSelector(variableProps, requiredProp)
      return acc
    },
    {},
  )

  return Object.keys(measurementSerialize).map(
    (key) => measurementSerialize[key],
  )
}

const orderSizeLabel = (rows) => {
  const labelList = Object.keys(rows[0] || []).filter((key) => key !== 'size')

  if (labelList.every((item) => !Number.isNaN(parseInt(item, 10)))) {
    return labelList.sort()
  }

  return labelList
}

const getTableColumns = (rows, customFirstTHeader) => {
  const tableFirstHeader = customFirstTHeader || SIZE_GUIDES_TABLE.columns[0]

  return [tableFirstHeader].concat(
    orderSizeLabel(rows).map((key) => ({
      key,
      title: key,
      ...(key === ' ' && { extend: true }),
    })),
  )
}

const injectExtendLength = (attributes, columnList) => {
  const possibleSize = attributes.length
  const cellSeparator = possibleSize - 1 // 1px wide each
  const cellWidth = BASE_CELL_WIDTH + CONTENT_ROW_PADDING
  const tableHeaderWidth =
    possibleSize * cellWidth - CONTENT_ROW_PADDING + cellSeparator

  columnList.map((col) => {
    const newCol = col
    if (newCol.key === ' ') {
      newCol.extend = tableHeaderWidth
    }
    return newCol
  })
}

const getColumnsWithInjectWidth = (
  rows,
  customFirstTableHeader,
  attributes,
  isShoeDualTable,
) => {
  const cols = getTableColumns(rows, customFirstTableHeader)

  if (isShoeDualTable) {
    injectExtendLength(attributes, cols)
  }

  return cols
}

export {
  getColumnsWithInjectWidth,
  getTableColumns,
  getTableRows,
  orderSizeLabel,
  translateBodyPart,
  translateMeasurementType,
}
